import { SystemStyleObject } from '@chakra-ui/react';
import {
  getTypographyToken,
  ColorMode,
  getBoxSizingStyles,
} from '@allergan-data-labs/alle-elements-core';
import type { ChipColorScheme, ChipSize } from './chip-types';

const baseChipStyle: SystemStyleObject = {
  ...getBoxSizingStyles(),
  ...getTypographyToken('Body/Extra small/Regular'),
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  verticalAlign: 'middle',
  cursor: 'pointer',
  maxW: '250px',
  borderWidth: '1px',
  borderStyle: 'solid',
};

const sizes = {
  sm: {
    height: '24',
    py: '3',
    px: '4',
    borderRadius: 'Circle',
  },
  lg: {
    height: '32',
    p: '4',
    borderRadius: 'Small',
  },
};

const getChipSize = (size: ChipSize) => sizes[size] || sizes.sm;

const getVariantStyles = (
  colorMode: ColorMode
): { [key in ChipColorScheme]: SystemStyleObject } => ({
  brand: {
    bg: `${colorMode}.Container/Brand/Transparent interactive/Enable`,
    color: `${colorMode}.Text/Brand/Default`,
    borderWidth: '1px',
    borderStyle: 'solid',
    borderColor: `${colorMode}.Border/Brand/Medium 3`,
    _hover: {
      bg: `${colorMode}.Container/Brand/Transparent interactive/Hover`,
    },
    _active: {
      bg: `${colorMode}.Container/Brand/Transparent interactive/Pressed`,
    },
    _focusVisible: {
      bg: `${colorMode}.Container/Brand/Transparent interactive/Hover`,
      outlineColor: `${colorMode}.Border/Brand/Focus ring/Default`,
      outlineStyle: 'solid',
      outlineWidth: '4px',
    },
  },
  muted: {
    bg: `${colorMode}.Container/Neutral/Transparent interactive/Mute enable`,
    color: `${colorMode}.Icon/Neutral/Support`,
    borderColor: `${colorMode}.Border/Neutral/Subtle 2`,
    _hover: {
      bg: `${colorMode}.Container/Neutral/Transparent interactive/Mute hover`,
    },
    _active: {
      bg: `${colorMode}.Container/Neutral/Transparent interactive/Mute pressed`,
    },
    _focusVisible: {
      bg: `${colorMode}.Container/Neutral/Transparent interactive/Mute hover`,
      outlineColor: `${colorMode}.Border/Brand/Focus ring/Default`,
      outlineStyle: 'solid',
      outlineWidth: '4px',
    },
  },
  error: {
    bg: `${colorMode}.Container/Transparent/Clear`,
    color: `${colorMode}.Text/Status/Error medium 3`,
    borderColor: `${colorMode}.Border/Status/Error medium 3`,
    cursor: 'text',
    _hover: {},
    _active: {},
    _focus: {},
    _focusVisible: {},
  },
});

export const getChipStyles = (
  variant: ChipColorScheme,
  colorMode: ColorMode,
  size: ChipSize
): SystemStyleObject => ({
  ...baseChipStyle,
  ...getChipSize(size),
  ...getVariantStyles(colorMode)[variant],
});

export const getDisabledStyles = (colorMode: ColorMode) => ({
  bg: `${colorMode}.Container/Neutral/Disabled/Default`,
  color: `${colorMode}.Text/Neutral/Disabled/Default`,
  borderColor: `${colorMode}.Border/Neutral/Disabled/Default`,
  cursor: 'text',
  _hover: {},
  _active: {},
  _focusVisible: {},
});

export const getChipIconStyles = () => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '16',
  width: '16',
  mr: '2',
});

export const getChipTextStyles = () => ({
  p: '4',
});

export const getChipCloseButtonStyles = ({
  colorMode = 'light',
  colorScheme = 'brand',
}: {
  colorMode?: ColorMode;
  colorScheme?: ChipColorScheme;
}): SystemStyleObject => {
  return {
    ...getVariantStyles(colorMode)[colorScheme],
    borderWidth: '0',
    borderRadius: 'Circle',
    minWidth: '13.333px',
    height: 'fit-content',
    p: 0,
    ml: '2',
    outlineOffset: '-2px',
    _active: {
      borderWidth: '0',
    },
    _hover: {
      borderWidth: '0',
    },
    _focusVisible: {
      outlineColor: `${colorMode}.Border/Brand/Focus ring/Default`,
      outlineStyle: 'solid',
      outlineWidth: '4px',
      boxShadow: 'none',
    },
    _disabled: {
      ...getDisabledStyles(colorMode),
    },
  };
};
